// Figma design tokens for the revamped list screens (EuroSafe design file:
// Objecten node 6033:6987, Contactpersonen node 6039:9894). Kept out of the MUI
// theme on purpose — the theme still drives the rest of the app, and these
// screens are being migrated one at a time.
export const C = {
    primary: "#130922",
    primaryLight: "#F9F6FF",
    jetblack: "#312F2F",
    n100: "#F8F9FA",
    n200: "#E9ECEF",
    n500: "#A5ABB1",
    n600: "#6C757D",
    n700: "#495057",
    subtle: "#5A6175",
    bodyText: "#212529",
    red: "#E52521",
    redStrong: "#C53030",
    warning: "#F09A3E",
    warningBg: "#FFECD7",
};
// The design's five-layer elevations ("n1" effect style for the cards, a much
// tighter one for the primary button). The far offsets are near-transparent in
// Figma too — the visible lift comes from the last two layers.
export const SHADOW_ACTIONS = "1px 728px 102px rgba(0,0,0,0), 0px 466px 93px rgba(0,0,0,0.01), 0px 262px 78.5px rgba(0,0,0,0.05), 0px 117px 58.5px rgba(0,0,0,0.09), 0px 29px 32px rgba(0,0,0,0.1)";
export const SHADOW_TABLE = "0px 717px 201px rgba(19,9,34,0), 0px 459px 184px rgba(19,9,34,0.01), 0px 258px 155px rgba(19,9,34,0.05), 0px 115px 115px rgba(19,9,34,0.09), 0px 29px 63px rgba(19,9,34,0.1)";
export const SHADOW_BUTTON = "0px 21px 6px rgba(0,0,0,0), 0px 14px 5px rgba(0,0,0,0.01), 0px 8px 5px rgba(0,0,0,0.05), 0px 3px 3px rgba(0,0,0,0.09), 0px 1px 2px rgba(0,0,0,0.1)";
// Column header label: 14/700, 1px tracking, uppercase.
export const headerCellSx = {
    fontFamily: '"Rubik", sans-serif',
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: "1px",
    color: C.jetblack,
    whiteSpace: "nowrap",
    textTransform: "uppercase",
};
// The design's 32px-high row action button (white, hairline border, 12px label).
export const smallButtonSx = {
    height: 32,
    minWidth: 0,
    borderRadius: "6px",
    textTransform: "none",
    fontSize: "12px",
    letterSpacing: "0.4px",
    fontWeight: 400,
    paddingX: "12px",
    paddingY: "6px",
    backgroundColor: "#fff",
    borderColor: C.n200,
    color: C.primary,
    "&:hover": { backgroundColor: C.n100, borderColor: C.n500 },
};
// 32px square row action button (the design's icon-only row button).
export const iconActionSx = {
    width: 32,
    height: 32,
    padding: "8px",
    borderRadius: "6px",
    border: `1px solid ${C.n200}`,
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: C.n100 },
};
// Search field / sort select: 56px tall, r12, on the light neutral fill.
export const filterFieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: C.n100,
        minHeight: 56,
        paddingX: "12px",
        transition: "box-shadow 0.2s ease",
        "& fieldset": { borderColor: "transparent" },
        "&:hover fieldset": { borderColor: "transparent" },
        "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(255, 186, 0, 0.45)" },
        "&.Mui-focused fieldset": { borderColor: "transparent" },
    },
    "& .MuiOutlinedInput-input": {
        fontSize: "14px",
        letterSpacing: "1px",
        color: C.n700,
    },
};
// Confirmation dialog paper (Figma "Unlink-Popover", node 6037:34735).
export const confirmDialogPaperSx = {
    padding: "16px",
    width: 360,
    maxWidth: "100%",
    margin: "16px",
    borderRadius: "12px",
    border: `1px solid ${C.n200}`,
    boxShadow: "0px 8px 24px rgba(19, 9, 34, 0.12)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
};
// Pill-shaped status chips (Figma Omzettoewijzing, node 6039:13733): 100px
// radius, 12/6 padding, 14px medium label on a tinted fill. Keyed by the MUI
// Chip colour the presentation helpers already return, so the tested mapping in
// sentBySource.ts stays untouched.
export const STATUS_CHIP_TONES = {
    success: { bg: "#EEFFF1", fg: "#2AA241" },
    warning: { bg: "#FFF1E2", fg: C.warning },
    info: { bg: "#E8F1F8", fg: "#005892" },
    default: { bg: C.primaryLight, fg: C.primary },
};
export const statusChipSx = (tone) => ({
    display: "inline-flex",
    alignItems: "center",
    paddingX: "12px",
    paddingY: "6px",
    borderRadius: "100px",
    backgroundColor: tone.bg,
    color: tone.fg,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: 1.43,
    letterSpacing: "1px",
    whiteSpace: "nowrap",
});
// Linked-leads panel (Figma "linked-leads-panel", node 6041:33179).
export const leadsPanelPaperSx = {
    marginTop: "8px",
    padding: "12px",
    minWidth: 320,
    maxWidth: 420,
    borderRadius: "16px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.12)",
};
// Destructive confirm button inside those dialogs.
export const destructiveButtonSx = {
    height: 32,
    minWidth: 0,
    paddingX: "12px",
    paddingY: "6px",
    borderRadius: "6px",
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 600,
    backgroundColor: C.redStrong,
    boxShadow: "none",
    "&:hover": { backgroundColor: "#A82626", boxShadow: "none" },
    "& .MuiButton-startIcon": { marginRight: "4px" },
};
